/*
 * Convert between the API `ProductConfigPayload` and the editor's working draft.
 *
 * The slimmed form (U3) carries only flat scalar fields, so the draft↔payload
 * conversion is straight string handling — no repeating-row editors remain.
 * Serialize trims, uppercases the column letter + geo country, and sends `null`
 * for cleared nullable scalars (the schema's `string | null`). The scheduled
 * goal/destination/event are PREFILLED with editable defaults in `toDraft` so a
 * fresh product shows them without retyping.
 */

import type { Schemas } from "../../api/client";
import type { ProductConfigPayload } from "./validation";

type MasterSheet = Schemas["MasterSheet"];
type ScheduledConfig = Schemas["ScheduledConfig"];

/** Editable defaults prefilled in the Scheduled conversion section (R7). */
export const SCHEDULED_DEFAULTS = {
  optimisation_goal: "OFFSITE_CONVERSIONS",
  destination: "WEBSITE",
  event: "PURCHASE",
  geo_country: "US",
} as const;

/** The AD_NAMING column when none is configured (matches the server default). */
export const DEFAULT_AD_NAMING_COLUMN = "V";

export interface ProductDraft {
  // identity & targeting
  notion_product: string;
  notion_data_source_id: string;
  account_id: string;
  /** FunnelFlux funnel ids, comma-separated in the form (2026-10-28). */
  funnelflux_funnel_ids: string;
  /** Stuck-ticket digest recipient, a Slack member ID (2026-10-30). */
  digest_slack_user_id: string;
  // master sheet
  sheet_id: string;
  sheet_tab: string;
  data_start_row: string;
  // naming — the single AD_NAMING column letter (e.g. "V")
  ad_naming_column: string;
  /** Display currency symbol for reporting (Creative Analysis); "" ⇒ "$". */
  currency: string;
  // scheduled conversion
  pixel_id: string;
  optimisation_goal: string;
  destination: string;
  event: string;
  geo_country: string;
  /** Past-buyer custom-audience id excluded at scheduled adset creation (#8). */
  exclusion_audience_id: string;
  /** Adset bid strategy for scheduled ABO launches ("" ⇒ inherit campaign). */
  bid_strategy: string;
  /** Cost/bid-cap amount in the account currency (string in the form; "" ⇒ none). */
  bid_amount: string;
  /** Attribution token for created adsets ("" ⇒ Meta's account default). */
  attribution_setting: string;
  /** Billing event for created adsets ("" ⇒ omitted). */
  billing_event: string;
  /** Daily budget in the account currency (string in the form; "" ⇒ none). */
  daily_budget: string;
  /** Advanced: JSON object of extra create-adset fields ("" ⇒ none). */
  create_extras: string;
  // launch-hygiene defaults (per-product)
  default_headline: string;
  utm_template: string;
  ensure_body_link: boolean;
  /** 2026-10-26: background checks (ticket hygiene) skip a paused product. */
  paused: boolean;
  // Settings-prefill fallbacks (2026-07-07 prefill v2) — served by
  // GET /launch/last-defaults when the product has no qualifying prior run.
  default_link: string;
  default_page_id: string;
}

/**
 * `A` → 1, `Z` → 26, `AA` → 27 … (1-indexed). Returns 0 for an empty/invalid
 * letter. Mirrors the backend letter→column conversion (KTD-5).
 */
export function letterToIndex(letter: string): number {
  const s = letter.trim().toUpperCase();
  if (!/^[A-Z]{1,2}$/.test(s)) return 0;
  let n = 0;
  for (const ch of s) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n;
}

/** 1 → `A`, 26 → `Z`, 27 → `AA` … Returns `""` for a non-positive index. */
export function indexToLetter(index: number): string {
  if (!Number.isInteger(index) || index <= 0) return "";
  let n = index;
  let out = "";
  while (n > 0) {
    const rem = (n - 1) % 26;
    out = String.fromCharCode(65 + rem) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
}

/** Build an editable draft from an existing payload (or blank defaults). */
export function toDraft(payload: ProductConfigPayload | undefined): ProductDraft {
  const p: Partial<ProductConfigPayload> = payload ?? {};
  const sheet: MasterSheet = p.master_sheet ?? { data_start_row: 2 };
  const sched: ScheduledConfig = p.scheduled ?? {};

  // KTD-7: the API read returns the stored "act_<digits>" value, but the form
  // now accepts bare digits only. Strip the leading "act_" so the input shows
  // the bare id; fromDraft() sends back bare digits and the server re-adds "act_".
  const storedAccountId = p.account_id ?? "";
  const draftAccountId = storedAccountId.startsWith("act_")
    ? storedAccountId.slice(4)
    : storedAccountId;

  return {
    notion_product: p.notion_product ?? "",
    notion_data_source_id: p.notion_data_source_id ?? "",
    account_id: draftAccountId,
    funnelflux_funnel_ids: (p.funnelflux_funnel_ids ?? []).join(", "),
    digest_slack_user_id: p.digest_slack_user_id ?? "",
    sheet_id: sheet.sheet_id ?? "",
    sheet_tab: sheet.tab ?? "",
    data_start_row:
      sheet.data_start_row != null ? String(sheet.data_start_row) : "2",
    ad_naming_column: (p.ad_naming_column ?? DEFAULT_AD_NAMING_COLUMN).toUpperCase(),
    currency: p.currency ?? "",
    pixel_id: sched.pixel_id ?? "",
    // Prefill the editable scheduled defaults when the stored value is absent (R7).
    optimisation_goal: sched.optimisation_goal ?? SCHEDULED_DEFAULTS.optimisation_goal,
    destination: sched.destination ?? SCHEDULED_DEFAULTS.destination,
    event: sched.event ?? SCHEDULED_DEFAULTS.event,
    geo_country: (sched.geo_country ?? SCHEDULED_DEFAULTS.geo_country).toUpperCase(),
    exclusion_audience_id: sched.exclusion_audience_id ?? "",
    bid_strategy: sched.bid_strategy ?? "",
    bid_amount: sched.bid_amount != null ? String(sched.bid_amount) : "",
    attribution_setting: sched.attribution_setting ?? "",
    billing_event: sched.billing_event ?? "",
    daily_budget: sched.daily_budget != null ? String(sched.daily_budget) : "",
    create_extras: sched.create_extras ?? "",
    // Launch-hygiene defaults ride flat fields on the payload; ensure_body_link
    // defaults ON for all products, existing included (KTD-4).
    default_headline: p.default_headline ?? "",
    utm_template: p.utm_template ?? "",
    ensure_body_link: p.ensure_body_link ?? true,
    paused: p.paused ?? false,
    default_link: p.default_link ?? "",
    default_page_id: p.default_page_id ?? "",
  };
}

/** `""` → `null` for the schema's nullable scalar string fields. */
function orNull(value: string): string | null {
  const t = value.trim();
  return t === "" ? null : t;
}

/** A positive account-currency number from a form string; blank/invalid → null. */
function numOrNull(value: string): number | null {
  const t = value.trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Serialize a draft into the exact `ProductConfigPayload` the API accepts. */
export function fromDraft(draft: ProductDraft): ProductConfigPayload {
  return {
    notion_product: orNull(draft.notion_product),
    notion_data_source_id: orNull(draft.notion_data_source_id),
    account_id: orNull(draft.account_id),
    funnelflux_funnel_ids: draft.funnelflux_funnel_ids
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter((s) => s !== ""),
    digest_slack_user_id: orNull(draft.digest_slack_user_id),
    master_sheet: {
      sheet_id: orNull(draft.sheet_id),
      tab: orNull(draft.sheet_tab),
      data_start_row: Number.parseInt(draft.data_start_row, 10) || 2,
    },
    // Uppercase on serialize (the server also uppercases — KTD-5 parity).
    ad_naming_column:
      draft.ad_naming_column.trim().toUpperCase() || DEFAULT_AD_NAMING_COLUMN,
    // Display currency for reporting surfaces; blank clears to null (server → "$").
    currency: orNull(draft.currency),
    scheduled: {
      pixel_id: orNull(draft.pixel_id),
      optimisation_goal: orNull(draft.optimisation_goal),
      destination: orNull(draft.destination),
      event: orNull(draft.event),
      geo_country: orNull(draft.geo_country.toUpperCase()),
      exclusion_audience_id: orNull(draft.exclusion_audience_id),
      bid_strategy: orNull(draft.bid_strategy.toUpperCase()),
      // Account-currency number (e.g. 55 = $55.00); blank/invalid clears to null.
      bid_amount: numOrNull(draft.bid_amount),
      attribution_setting: orNull(draft.attribution_setting),
      billing_event: orNull(draft.billing_event.toUpperCase()),
      daily_budget: numOrNull(draft.daily_budget),
      create_extras: orNull(draft.create_extras),
    },
    // Launch-hygiene per-product defaults (U6): nullable free-text strings clear
    // to null when blank; ensure_body_link defaults ON for all products (KTD-4).
    default_headline: orNull(draft.default_headline),
    utm_template: orNull(draft.utm_template),
    ensure_body_link: draft.ensure_body_link,
    paused: draft.paused,
    default_link: orNull(draft.default_link),
    default_page_id: orNull(draft.default_page_id),
  };
}
